import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, useDispatch } from 'react-redux';
import { getTag } from '@Helpers';

import { CredentialCardProps, ErrorMessage, Loading, withValidConfiguration } from '@Components';
import { useFetchData } from '@CustomHooks';
import { ConnectorConfig, ProviderCredential } from '@Models';
import { bopsApi } from '@Network';
import { AppDispatch, fetchExpiringCredentials, RootState } from '@Store';
import config from '../../config';
import { adaptConnectorsFromApi, resolveConnectorKey } from './connectorRegistry';
import CredentialsComponent from './credentials';

// Build-time constant — derived from env at bundle time, never changes at runtime.
const IS_GS1_FLAVOR = config.isGs1Flavor;

const CredentialsContainer = ({
  tags,
  glnType,
  defaultConnectors,
}: {
  tags: string[];
  glnType?: string;
  defaultConnectors: string[];
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  const { data, isLoading, error, refetch } = useFetchData(bopsApi.getCredentials, tags);
  const {
    data: connectorMetadata,
    isLoading: isLoadingConnectors,
    error: connectorsError,
  } = useFetchData(bopsApi.getConnectors);

  const connectorMap = useMemo<Record<string, ConnectorConfig>>(
    () => (connectorMetadata ? adaptConnectorsFromApi(connectorMetadata, t) : {}),
    [connectorMetadata, t],
  );

  const resolvedDefaultConnectors = useMemo(() => defaultConnectors.map(resolveConnectorKey), [defaultConnectors]);

  // GS1 SMB providers self-onboard Walmart + Soriana regardless of their stored
  // tags — inject them here so the rest of the pipeline (matching credentials,
  // fallback "Available" cards) handles them uniformly. Keeps `tags` (the cache
  // key for getCredentials) untouched. Compares case-insensitively so a backend
  // tag like "Walmart" doesn't end up iterating twice alongside our lowercase
  // injection.
  const effectiveTags = useMemo(() => {
    const isGs1SmbProvider = IS_GS1_FLAVOR && glnType === 'provider';
    let result = tags;

    if (isGs1SmbProvider) {
      const smbTags = ['walmart', 'soriana'];
      const lowercasedTags = new Set(tags.map((tag) => tag.toLowerCase()));
      const missingSmbTags = smbTags.filter((tag) => !lowercasedTags.has(tag));
      result = [...tags, ...missingSmbTags];
    }

    if (IS_GS1_FLAVOR && resolvedDefaultConnectors.length > 0) {
      const lowered = new Set(result.map((tag) => tag.toLowerCase()));
      const missingDefaults = resolvedDefaultConnectors.filter((dc) => !lowered.has(dc.toLowerCase()));
      result = [...result, ...missingDefaults];
    }

    return result;
  }, [tags, glnType, resolvedDefaultConnectors]);

  const credentials = useMemo<CredentialCardProps[]>(() => {
    if (!data) return [];

    return effectiveTags.flatMap((tag) => {
      let matchingCredentials = data.filter(
        (credential: ProviderCredential) => getTag(credential.connectorType) === tag,
      );
      let fallbackConnectorType = Object.keys(connectorMap).filter((ctype) => getTag(ctype) === tag);

      if (tag.toLowerCase() === 'walmart') {
        if (IS_GS1_FLAVOR) {
          matchingCredentials = matchingCredentials.filter((c) => c.connectorType.toLowerCase() === 'gs1_wrl');
          fallbackConnectorType = fallbackConnectorType.filter((c) => c.toLowerCase() === 'gs1_wrl');
        } else {
          matchingCredentials = matchingCredentials.filter((c) => c.connectorType.toLowerCase() === 'wrl');
          fallbackConnectorType = fallbackConnectorType.filter((c) => c.toLowerCase() === 'wrl');
        }
      }

      let result: CredentialCardProps[] = [];

      if (matchingCredentials.length > 0) {
        result = matchingCredentials.flatMap((connectorCredential) => {
          const connectorConfig = connectorMap[connectorCredential.connectorType.toLowerCase()];
          if (!connectorConfig) return [];
          return [{ credential: connectorCredential, ...connectorConfig }];
        });
      }

      if (fallbackConnectorType.length > 0) {
        const fallbackCredentials = fallbackConnectorType.map((ctype) => {
          const isDefault = resolvedDefaultConnectors.some((dc) => dc === ctype.toLowerCase());
          return {
            credential: {
              connectorType: ctype,
              accountEmail: '',
              accountPassword: '',
              token: '',
              account: '',
              credentialUpdatedAt: '',
              // truthy createdAt → buildCardProps shows this card as connected (see buildCardProps.ts:17)
              createdAt: isDefault ? 'auto' : '',
              status: isDefault ? 'ACTIVE' : 'PENDING',
            },
            ...connectorMap[ctype.toLowerCase()],
          };
        });

        const filteredFallbacks = fallbackCredentials.filter(
          (fallbackCred) =>
            !result.some(
              (existingCred) =>
                existingCred.credential.connectorType.toLowerCase() ===
                fallbackCred.credential.connectorType.toLowerCase(),
            ),
        );

        result = [...result, ...filteredFallbacks];
      }

      return result;
    });
  }, [effectiveTags, data, connectorMap, resolvedDefaultConnectors]);

  const credentialUpdated = useCallback(() => {
    // Fire-and-forget — both functions handle their own errors internally
    // (refetch via useFetchData try/catch → sendErrorEvent; thunk via Redux failure action).
    refetch();
    dispatch(fetchExpiringCredentials());
  }, [refetch, dispatch]);

  if ((isLoading && !data) || isLoadingConnectors) return <Loading title={t('Credentials.Loading')} />;
  if (error || !data || connectorsError)
    return <ErrorMessage internalMessage={`Unable to get credentials: ${String(error ?? connectorsError)}`} />;

  return <CredentialsComponent credentials={credentials} credentialUpdated={credentialUpdated} />;
};

const mapStateToProps = (state: RootState) => {
  const { client } = state;
  const { configuration } = client;
  const { tags, glnType, defaultConnectors } = configuration || {};

  return {
    tags: tags || [],
    glnType,
    defaultConnectors: defaultConnectors || [],
  };
};

export default withValidConfiguration(connect(mapStateToProps)(CredentialsContainer), 'tags');
